import { FiatAssetDto, TokenAssetDto } from '@/types/assets';
import { Credential } from '../credentials';
import { PaymentPlatform } from '@/api/__types/invoices.types';
import { DefaultOptionType } from 'antd/es/select';

export interface ICreateAccountPayload {
  name: string;
  type: string;
  source: string;
  provider: string;
  chain?: string;
  address?: string;
  refId?: string;
  symbol?: string;
  syncStartAt?: string;
}

export interface AccountBalance {
  id: number;
  icon?: string | null;
  symbol?: string | null;
  tokenId?: number;
  name?: string | null;
  priceSource?: string | null;
  bookAccount: string | null;
  amount: string | null;
  costBasis: string | null;
  fiatAmount: string | null;
  unrealizedReturn: string | null;
  unrealizedReturnPercent: string | null;
  updatedAt?: Date | null;
  asset?: TokenAssetDto;
}

// What type of account is it
export enum AccountType {
  wallet = 'wallet',
  vault = 'vault',
  safe = 'safe',
  contract = 'contract',
  fiat = 'fiat',
  exchange = 'exchange',
  csv_import = 'csv_import',
  third_party_format_import = 'third_party_format_import',

  /**
   * An account holding synthetic transactions linked to a main account
   * (see `mainAccount` field).
   * The first use case is for DeFi staking involving rewards that are
   * accrued in the contract until it's claimed. Some clients want to see
   * these rewards as they are accrued in the contract instead of the whole
   * chunk of rewards when it's claimed. So, we're storing these rewards
   * as synthetic transactions in the virtual account.
   */
  virtual = 'virtual',
}

// Which icon to use for the UI? Where did the account come from?
// This is the branding behind the account, how the user easily identifies with it.
// An account can be from a particular provider, but the data may be
// synced from a different source. For example, the provider could be Binance, but
// the data is synced from Vezgo or some sort of aggregate API service.
export enum AccountProvider {
  metamask = 'metamask',
  ledger = 'ledger',
  anchorage = 'anchorage',
  falconx = 'falconx',
  fireblocks = 'fireblocks',
  ftx = 'ftx',
  ftxus = 'ftxus',
  gnosis = 'gnosis',
  kraken = 'kraken',
  cardano = 'cardano',
  coinbase = 'coinbase',
  coinbasepro = 'coinbasepro',
  coinbaseadvanced = 'coinbaseadvanced',
  coinbasecustody = 'coinbasecustody',
  coinbasecommerce = 'coinbasecommerce',
  coinbasecommerce_csv = 'coinbasecommerce_csv',
  coinbaseprime = 'coinbaseprime',
  paxos = 'paxos',
  phantom = 'phantom',
  gemini = 'gemini',
  qredo = 'qredo',
  squads = 'squads',
  shape = 'shape',
  csv_import = 'csv_import',
  binance = 'binance',
  bybit = 'bybit',
  okx = 'okx',
  circle = 'circle',
  utila = 'utila',
  fordefi = 'fordefi',
  gridplus = 'gridplus',
  arweave = 'arweave',
  cosmosHub = 'cosmoshub-4',
  osmosis = 'osmosis-1',
  dydx = 'dydx-mainnet-1',
  neutron = 'neutron-1',
  noble = 'noble-1',
  flow = 'flow',
  ripple = 'ripple',
  m2 = 'm2',
  backpack = 'backpack',
  cobo = 'cobo',
  // For fiat accounts, the bank will be represented in the ref_id property via the institution id
  financialInstitution = 'financialinstitution',
  bitgo = 'bitgo',
  /** Virtual account created by integral (org-scoped, not tied to any account) */
  virtual = 'virtual',
  // generic provider that encapsulates crypto wallet addresses that get added manually through the interface
  crypto_wallet = 'crypto_wallet',
}

// What connection are we using to get the data?
// This could be an API that aggregates data for us (ex: vezgo, plaid),
// could be how we get data directly from chains or API built specifically for the chain (ex: ethereum), or could be
// an exchange or platform API we built into for a provider (ex: anchorage)
export enum AccountSource {
  anchorage = 'anchorage',
  anchorage_lending_import = 'anchorage_lending_import',
  cardano = 'cardano',
  coinbase = 'coinbase',
  coinbasepro = 'coinbasepro',
  coinbaseadvanced = 'coinbaseadvanced',
  coinbasecustody = 'coinbasecustody',
  coinbasecommerce = 'coinbasecommerce',
  coinbaseprime = 'coinbaseprime',
  coinbaseprimev2 = 'coinbaseprimev2',
  paxos = 'paxos',
  falconx = 'falconx',
  fireblocks = 'fireblocks',
  ftx = 'ftx',
  ftxus = 'ftxus',
  ethereum = 'ethereum',
  blast = 'blast',
  linea = 'linea',
  polygon = 'polygon',
  binanceSmartChain = 'binance-smart-chain',
  arbitrumOne = 'arbitrum-one',
  arbitrumNova = 'arbitrum-nova',
  solana = 'solana',
  gemini = 'gemini',
  hyperliquid = 'hyperliquid',
  csv_import = 'csv_import',
  kraken = 'kraken',
  binance = 'binance',
  avalanche = 'avalanche',
  bybit = 'bybit',
  okx = 'okx',
  circle = 'circle',
  mantle = 'mantle',
  mode = 'mode',
  monad = 'monad',
  optimism = 'optimism',
  zksyncEra = 'zksync-era',
  zora = 'zora',
  scroll = 'scroll',
  stellar = 'stellar',
  story = 'story',
  base = 'base',
  ripple = 'ripple',
  robinhood = 'robinhood',
  third_party_format_import = 'third_party_format_import',
  arweave = 'arweave',
  cosmosHub = 'cosmoshub-4',
  osmosis = 'osmosis-1',
  dydx = 'dydx-mainnet-1',
  flow = 'flow',
  plaid = 'plaid',
  bitgo = 'bitgo',
  m2 = 'm2',
  backpack = 'backpack',
  cobo = 'cobo',
  unichain = 'unichain',
  berachain = 'berachain',
  apechain = 'apechain',
  gnosis = 'xdai',
  abstract = 'abstract',
  neutron = 'neutron-1',
  noble = 'noble-1',
  sei = 'pacific-1',
  seiEvm = 'sei-v2',
  shape = 'shape',
  /** Virtual account created by integral (org-scoped, not tied to any account) */
  virtual = 'virtual',
}

export interface AccountReference {
  id: string;
  type: AccountType;
  name: string;
  icon?: string | null; // Currently only supported for Plaid
  provider: AccountProvider;
  source: AccountSource;
  chain?: string | null;
  address?: string | null;
  accountNumber: string | null;
  institutionName?: string;
  sectionId?: string | null; // `undefined` if not in a section
  credential?: Credential;
}

/**
 * Who initiated the current sync pause on a wallet. `user` means a client
 * paused via self-serve; `admin` means support / Safe Haven paused it on
 * the user's behalf. Only `user`-paused wallets can be resumed via the
 * self-serve API — admin pauses must be lifted by support.
 */
export type PauseSource = 'user' | 'admin';

export interface Account extends AccountReference {
  txnCount?: number | null;
  updatedAt?: Date | null;
  createdAt: Date;
  balances: AccountBalance[];
  bookAccount: string | null;
  totalCostBasis: string;
  totalFiatAmount: string;
  totalUnrealizedReturn: string;
  totalUnrealizedReturnPercent: string | null;
  owner?: string | null;
  numberOfCurrenciesMissingFromUnrealizedReturn: number;
  firstTxnTimestamp?: Date | null;
  lastTxnTimestamp?: Date | null;
  orderInSection?: number | null;
  description?: string | null;
  hasSubAccounts?: boolean; // for solana stake accounts
  totalSubAccountsAssets?: string;
  totalSubAccountsTxnCount?: number;
  subAccountsCount?: number;

  /**
   * Set when the wallet's transaction sync is currently paused. `null`
   * means syncing is active. Replaces the older boolean `isSyncPaused`
   * field — surfaces the actor so the UI can show the admin-paused state
   * (disabled resume + tooltip) distinctly from the user-paused state.
   */
  syncPauseSource?: PauseSource | null;

  // Missing from docs
  credentials?: Credential['id']; // crerdentialId
}

/**
 * Convenience predicate: "is this wallet currently paused (by anyone)?"
 * Keeps the null-check colocated with the type so callers don't have to
 * remember the sentinel value.
 */
export const isSyncPaused = (account: Pick<Account, 'syncPauseSource'>): boolean =>
  account.syncPauseSource != null;

/**
 * Convenience predicate: "can the current user self-serve resume this
 * wallet?". Admin-paused wallets must be resumed by support, so a `null`
 * source (not paused) also returns false here; only `'user'` is truthy.
 */
export const canResumeSync = (account: Pick<Account, 'syncPauseSource'>): boolean =>
  account.syncPauseSource === 'user';

export interface BulkPauseSyncDto {
  accountIds: string[];
}

/**
 * A wallet that was skipped by a pause request because it is currently
 * paused by Integral Support (admin-paused). The UI surfaces these so the
 * user knows their action didn't cover every wallet they selected.
 */
export interface SkippedAdminPausedWallet {
  id: string;
  name: string;
}

/**
 * Response from single/bulk/section pause-sync endpoints. `pausedCount`
 * reflects the wallets actually flipped by this request; admin-paused
 * wallets are not flipped and instead land in `skippedAdminPaused` so the
 * UI can surface them to the user.
 */
export interface PauseSyncResponse {
  pausedCount: number;
  skippedAdminPaused: SkippedAdminPausedWallet[];
}

export interface AccountWithChildren extends AccountReference {
  accounts?: Account[];
}

export interface PaymentAccount extends AccountReference {
  enabled: boolean;
  note: string;
  paymentType: PaymentPlatform | string;
}

export interface PaymentAccountOption extends DefaultOptionType {
  disabled: boolean;
  key: string;
  name: string;
  address: string;
  paymentType: PaymentPlatform | string;
}

export interface AccountName {
  id: string;
  name: string;
  provider: string;
  source: string;
}

export interface UpdateBalanceDto {
  id: string;
  bookAccountId: string;
}

export interface UpdateAccountBalancesDto {
  id: string;
  bookAccountId: string;
  balances: UpdateBalanceDto[];
}

export interface BulkUpdateAccountDto {
  id: string;
  name?: string;
  description?: string | null;
  owner?: string | null;
  sectionId?: string | null;
  orderInSection?: number | null;
}

export interface UpdateAccountDto {
  name?: string;
  description?: string | null;
  owner?: string | null;
  apiKey?: string | null;
  signingKey?: string | null;
  sectionId?: string | null;
  orderInSection?: number | null;
}

export interface BulkDeleteAccountDto {
  ids: string[];
}

export interface GetAccountsQuery {
  includeZero?: boolean;
  includeArchiveSectionAccounts?: boolean;
  includeIcons?: boolean; // currently only supported for Plaid
  includeAccountNumbers?: boolean; // currently only supported for Plaid
  excludeSubAccounts?: boolean;
  mainAccountId?: string;
  page?: number;
  pageSize?: number;
}

export interface ListAccountsQuery {
  includeIcons?: boolean; // currently only supported for Plaid
  includeAccountNumbers?: boolean; // currently only supported for Plaid
}

export interface WalletDto {
  address: string;
  balances: any[];
  bookAccount: string;
  chain: string;
  createdAt: string;
  id: string;
  name: string;
  tokens: any[];
  numberOfCurrenciesMissingFromUnrealizedReturn: number;
  provider: string;
  source: string;
  totalFiatAmount: string;
  totalUnrealizedReturn: string;
  totalUnrealizedReturnPercent: string;
  txnCount: number;
  type: string;
  updatedAt: string;
}

export interface WalletTokenByDayDto {
  symbol: string;
  tokenId: number;
  name: string;
  iconUrl: string | null;
  chain: string;
  balance: string;
  usdAmount: string;
  costBasis: string;
  unrealizedReturn: string;
  unrealizedReturnPercent: number | null;
  amount: string;
  asset?: TokenAssetDto;
}

export interface WalletBalanceDto {
  date: string;
  usdAmount: string;
  balance: string;
  costBasis: string;
  unrealizedReturn: string;
  unrealizedReturnPercent: number | null;
  accounts: WalletByDayDto[];
}

export interface WalletChainsDto {
  chains: string[];
  wallet: { id: string; address: string; name: string };
}

export interface WalletByDayDto {
  accountId: string;
  wallet: string;
  usdAmount: string;
  balance: string;
  costBasis: string;
  unrealizedReturn: string;
  unrealizedReturnPercent: number | null;
  credential?: Credential;
  tokens: WalletTokenByDayDto[];
  collections: {
    accountId: string;
    accountName: string;
    amount: string;
    collectionId: string;
    collectionName: string;
    thumbnailUrl: string | null;
    usdCostBasis: string;
  }[];
}

export interface CoinDto {
  name: string;
  quantity: string;
  symbol: string;
  tokenId: number;
  totalPrice: number;
  unrealizedReturn: string;
  unrealizedReturnPercent: string;
}

export type ActionMap<M extends { [index: string]: any }> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key;
      }
    : {
        type: Key;
        payload: M[Key];
      };
};

export interface CoinByDayDto {
  amount: string;
  costBasis: string;
  symbol: string;
  tokenId: number;
  usdAmount: string;
  asset?: TokenAssetDto;
}

export interface GetAccountsWalletBalancesDto {
  from?: string;
  to?: string;
  accountId?: string;
  tokenId?: number;
  includeArchiveSectionAccounts?: boolean;
}

export interface GetBalancesOvertimeDto {
  accountId?: string;
  tokenId?: number;
  from?: string;
  to?: string;
  includeArchiveSectionAccounts?: boolean;
}

export interface IncludeArchiveSectionsDto {
  includeArchiveSectionAccounts?: boolean;
}

export interface BalanceOverTime {
  date: string;
  balanceUsd: string;
}

export interface TokenBalanceDto {
  symbol: string;
  tokenId: number;
  name: string;
  quantity: string;
  iconUrl: string | null;
  costBasis: string | null;
  totalPrice: string | null;
  unrealizedReturn: string | null;
  unrealizedReturnPercent: number | null;
  asset: TokenAssetDto;
  accounts: AccountBalanceForTokenDto[] | null;
}

export interface TokenBalanceByDayDto {
  symbol: string;
  tokenId: number;
  amount: string;
  name: string;
  iconUrl: string | null;
  usdAmount: string | null;
  costBasis: string | null;
  unrealizedReturn: string | null;
  unrealizedReturnPercent: number | null;
  asset: TokenAssetDto;
  accounts: AccountBalanceForTokenDto[] | null;
}

export interface AccountBalanceForTokenDto {
  accountId: string;
  name: string;
  address: string | null;
  type: string;
  source: string;
  provider: string;
  chain: string | null;
  amount: string;
  usdAmount: string;
  costBasis: string | null;
  asset: TokenAssetDto;
  unrealizedReturn: string | null;
  unrealizedReturnPercent: number | null;
  credential?: Credential;
}

export interface TokensBalanceDto {
  date: string;
  usdAmount: string;
  tokens: TokenBalanceByDayDto[];
}

export interface BalanceCheck {
  id: number | string;
  account: string;
  tokenId: number;
  initialMismatchFoundAt?: Date;
  expectedValue: string; // "Reference Balance"
  actualValue: string; // "Integral Balance"
  ignore: boolean;
}

export interface BalanceCheckSnapshotGroup {
  id: string;
  createdAt: Date;
}

export enum AccountSectionType {
  Fiat = 'Fiat',
}

export interface AccountSection {
  id: string;
  name: string; // cannot be changed for "Archive"
  order: number;
  icon?: string | null; // cannot be changed for "Archive"
  iconColor?: string | null;
  isArchive: boolean;
  type?: AccountSectionType;
}

export interface CreateAccountSectionDto extends Partial<Omit<AccountSection, 'id' | 'isArchive'>> {
  // only `name` and `order` required
  name: AccountSection['name'];
  order: AccountSection['order'];
}

export interface BulkUpdateAccountSectionDto extends Partial<Omit<AccountSection, 'isArchive'>> {
  id: string;
}

export interface UpdateAccountSectionDto extends Omit<BulkUpdateAccountSectionDto, 'id'> {}

export enum DefiModuleType {
  Staking = 'Staking',
  YieldFarming = 'Yield Farming',
  Locked = 'Locked',
  Yield = 'Yield',
  Deposit = 'Deposit',
  LiquidityPool = 'Liquidity Pool',
  Lending = 'Lending',
  Vesting = 'Vesting',
  NftLending = 'NFT Lending',
  NftStaking = 'NFT Staking',
  Debt = 'Debt',
  Rewards = 'Rewards',
}

export const DEFI_BALANCE_TYPES = {
  Deposit: 'Deposit',
  Rewards: 'Rewards',
  Debt: 'Debt',
} as const;
export type DefiBalanceType = (typeof DEFI_BALANCE_TYPES)[keyof typeof DEFI_BALANCE_TYPES];

export enum DefiBalanceGroupBy {
  Protocol = 'Protocol',
  Account = 'Account',
}

export interface DefiBalancesQuery {
  date: string; // e.g. "2023-11-28"
  // lpTokenType?: string; // @deprecated use protocolType instead
  protocolType?: DefiModuleType;
  balanceType?: DefiBalanceType;
  groupBy?: DefiBalanceGroupBy; // default value: DefiBalanceGroupBy.Protocol;
}

export interface DefiProtocolDto {
  name: string;
  url?: string;
  logoUrl?: string;
}

export interface DefiBalanceDto {
  asset: TokenAssetDto;
  fiat: FiatAssetDto;
  chain?: string;
  type: DefiBalanceType;
  moduleType?: DefiModuleType;
}

export interface DefiPoolDto {
  chain: string;
  address: string; // e.g. 0x320b564fb9cf36933ec507a846ce230008631fd3
  name: string; // e.g. 'Curve.fi Factory Plain Pool: frxETHpETH'
}

export interface DefiBalancesForPool {
  pool: DefiPoolDto;
  balances: DefiBalanceDto[];
}

export interface DefiBalancesForProtocol {
  protocol: DefiProtocolDto;
  balancesByPool: DefiBalancesForPool[];
  balancesWithoutPool: DefiBalanceDto[];
}

export interface DefiBalancesForAccount {
  account: {
    id: string;
    name: string;
    provider: string;
    chain?: string;
    address?: string;
  };
  balancesByPool: DefiBalancesForPool[];
  balancesWithoutPool: DefiBalanceDto[];
}

export type DefiBalancesCountDto = { defiEnabledOn: string } & (
  | {
      protocolCount: number;
    }
  | { accountCount: number }
);

export interface TokenBalancesQuery {
  isToday: boolean;
  from: string;
  to: string;
  includeArchiveSectionAccounts?: boolean;
}

export interface FiatBalancesQuery {
  date: string; // e.g. '2023-11-28'
}

export interface FiatAssetBalanceDto extends FiatAssetDto {
  source: string; // e.g. Coinbase
  cost: string; //'100.123'
  value: string; // '100.123';
  logoUrl?: string | null; // e.g. base 64 encoded image'
}

export interface FiatBalancesForCurrency {
  currency: string;
  balances: FiatAssetBalanceDto[];
}

export interface FiatBalancesForSource {
  source: string;
  balances: FiatAssetBalanceDto[];
}
